/**
 * PWA del CMDB: registro del service worker y color de la barra del sistema.
 *
 * El service worker (`public/sw.js`) solo guarda el armazón de la aplicación
 * (HTML, JS, CSS e iconos). Los datos siempre vienen del servidor: el CMDB no
 * funciona sin conexión, y es a propósito, para no enseñar inventario viejo.
 */

const META_COLOR = 'meta[name="theme-color"]';

/**
 * Registra el service worker. Devuelve el registro o `null` si no se registra.
 *
 * Solo en compilación de producción: en `npm start` el service worker estorba
 * al recargado en caliente y deja ficheros viejos en caché entre cambios.
 */
export function registrarServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  if (process.env.NODE_ENV !== 'production') return Promise.resolve(null);
  // Los navegadores solo permiten service workers en HTTPS (o en localhost).
  if (!window.isSecureContext) return Promise.resolve(null);

  const registrar = () =>
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL || ''}/sw.js`, { scope: '/' })
      // Sin service worker la aplicación funciona igual: solo se pierde el
      // poder instalarla, así que no tiene sentido molestar al usuario.
      .catch(() => null);

  if (document.readyState === 'complete') return registrar();
  return new Promise((resolver) => {
    window.addEventListener('load', () => resolver(registrar()), { once: true });
  });
}

/**
 * Pinta la barra de estado del móvil con el color de fondo de la aplicación.
 *
 * Al instalar, Android e iOS toman el color del manifest, pero al abrirla usan
 * el `<meta name="theme-color">`: si no se sincroniza, la barra se queda del
 * color de fábrica aunque el usuario tenga puesta otra paleta.
 */
export function sincronizarColorDeBarra() {
  if (typeof document === 'undefined') return;
  const meta = document.querySelector(META_COLOR);
  if (!meta) return;
  const fondo = getComputedStyle(document.documentElement)
    .getPropertyValue('--slate-50')
    .trim();
  if (fondo) meta.setAttribute('content', fondo);
}
